import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import { useAuth } from "context/AuthContext";
import { post } from "lib/api";
import { isPortalRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

export default function CustomerDeleteAccount() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const { t } = useStorefrontCopy();
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const role = String(pluginRole || "").toLowerCase();
  const isPortal = isPortalRole(role);

  if (!token)
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  if (authLoading || pluginRole == null) return null;
  if (!isPortal) return <Navigate to="/dashboard" replace />;

  const submitRequest = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      await post("/api/storefront/me/delete-request", { data: {} });
      setConfirming(false);
      setMessage({
        type: "success",
        text: "Account deletion request submitted. We will review it and follow up by email.",
      });
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.response?.data?.error?.message ||
          error?.message ||
          "Unable to submit account deletion request.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomerPortalLayout
      title="Delete Account"
      subtitle="Request deletion of your customer or distributor account."
    >
      <Stack spacing={2}>
        {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <MDTypography variant="h5" fontWeight={900} color="error">
                Delete account
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text"
                sx={{ lineHeight: 1.8 }}
              >
                {t(
                  "profile.deleteRequestText",
                  "This sends a verified deletion request. We will review it and follow up by email."
                )}
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text"
                sx={{ lineHeight: 1.8 }}
              >
                Some order, invoice, payment, warranty, core return, support,
                subscription, and security records may be retained where
                required for legal, accounting, fraud-prevention, or operational
                reasons.
              </MDTypography>

              {confirming ? (
                <Alert severity="warning">
                  Confirm that you want to submit this account deletion request.
                </Alert>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {confirming ? (
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() => setConfirming(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </MDButton>
                ) : null}
                <MDButton
                  variant="outlined"
                  color="error"
                  onClick={submitRequest}
                  disabled={submitting}
                >
                  {submitting
                    ? "Submitting..."
                    : confirming
                    ? "Submit deletion request"
                    : "Delete account"}
                </MDButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </CustomerPortalLayout>
  );
}
